import { fromJS, List } from 'immutable';

import TeamMember from 'models/TeamMemberModel';
import Team from 'models/TeamModel';
import Entity from 'models/Entity';
import Network from 'models/NetworkModel';

import { normalizeModelList } from '../utils/Normalizer';
import { combineReducerArray } from '../utils/helpers';

import {
  myTeam,
  myNetwork,
  myCompany,
  editTeam,
  SET_TEAM,
  SET_COMPANY,
  SET_TEAM_MEMBERS,
  viewTeam,
} from './types';

import updateRoleReducerObject from './updateRole/reducer';
import myCompanyTeamsReducerObject from './myCompanyTeams/reducer';
import resendInviteReducerObject from './resendInvite/reducer';
import makeChampionReducerObject from './makeChampion/reducer';

export * from './actions';

const initialState = fromJS({
  teamsById: {},
  membersById: {},
  networksById: {},
  companiesById: {},
  // The team where the current user belongs
  myTeam: {
    id: null,
    network: { loading: false, error: false },
  },
  // The network where the current user belongs
  myNetwork: {
    id: null,
    network: { loading: false, error: false },
  },
  // The company where the current user belongs
  myCompany: {
    id: null,
    network: { loading: false, error: false },
  },
  // The company teams where the current user belongs and has access to
  myCompanyTeams: {
    data: [],
    network: { loading: false, error: false },
  },
  editTeam: {
    network: { loading: false, error: false },
  },
  updateRole: {
    // [userId]: {
    //     network: { loading: false, error: false },
    // },
  },
  teams: {
    // [teamId]: {
    // network: { loading: false, error: false },
    // }
  },
  resendInviteModal: {
    isOpen: false,
    teamMember: false,
    network: { loading: false, error: false },
  },
  makeChampionModal: {
    isOpen: false,
    team: null,
    teamMember: null,
    network: { loading: false, error: false },
  },
});

const requestMyNetworkSuccessReducer = (state, action) => {
  const usersList = action.response.entities.reduce(
    (acc, entity) => [
      ...acc,
      ...entity.teams.reduce((acc2, team) => (team.users ? [...acc2, ...team.users] : acc2), []),
    ],
    [],
  );
  const teamsList = action.response.entities.reduce((acc, entity) => [...acc, ...entity.teams], []);
  return state.withMutations((s) =>
    s
      .setIn(['myNetwork', 'network', 'loading'], false)
      .setIn(['myNetwork', 'network', 'error'], false)
      .setIn(['myNetwork', 'id'], action.response.id)
      .mergeIn(['membersById'], normalizeModelList(usersList, TeamMember.deserialize))
      .mergeIn(
        ['teamsById'],
        normalizeModelList(teamsList, (...args) => Team.deserialize(...args).normalize()),
      )
      .mergeIn(
        ['companiesById'],
        normalizeModelList(action.response.entities, (...args) =>
          Entity.deserialize(...args).normalize(),
        ),
      )
      .mergeIn(
        ['networksById'],
        normalizeModelList([action.response], (...args) =>
          Network.deserialize(...args).normalize(),
        ),
      ),
  );
};

const requestMyCompanySuccessReducer = (state, action) => {
  const usersList = action.response.teams.reduce(
    (acc, team) => (team.users ? [...acc, ...team.users] : acc),
    [],
  );
  return state.withMutations((s) =>
    s
      .setIn(['myCompany', 'network', 'loading'], false)
      .setIn(['myCompany', 'network', 'error'], false)
      .setIn(['myCompany', 'id'], action.response.id)
      .mergeIn(['membersById'], normalizeModelList(usersList, TeamMember.deserialize))
      .mergeIn(
        ['teamsById'],
        normalizeModelList(action.response.teams, (...args) =>
          Team.deserialize(...args).normalize(),
        ),
      )
      .mergeIn(
        ['companiesById'],
        normalizeModelList([action.response], (...args) => Entity.deserialize(...args).normalize()),
      ),
  );
};

const teamReducerObject = {
  [myTeam.request]: (state) => state.setIn(['myTeam', 'network', 'loading'], true),
  [myTeam.success]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['myTeam', 'network', 'loading'], false)
        .setIn(['myTeam', 'network', 'error'], false)
        .setIn(['myTeam', 'id'], action.response.id)
        .mergeIn(['membersById'], normalizeModelList(action.response.users, TeamMember.deserialize))
        .mergeIn(
          ['teamsById'],
          normalizeModelList([action.response], (...args) => Team.deserialize(...args).normalize()),
        ),
    ),
  [myTeam.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['myTeam', 'network', 'loading'], false)
        .setIn(['myTeam', 'network', 'error'], action.error),
    ),

  [myNetwork.request]: (state) => state.setIn(['myNetwork', 'network', 'loading'], true),
  [myNetwork.success]: requestMyNetworkSuccessReducer,
  [myNetwork.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['myNetwork', 'network', 'loading'], false)
        .setIn(['myNetwork', 'network', 'error'], action.error),
    ),

  [myCompany.request]: (state) => state.setIn(['myCompany', 'network', 'loading'], true),
  [myCompany.success]: requestMyCompanySuccessReducer,
  [myCompany.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['myCompany', 'network', 'loading'], false)
        .setIn(['myCompany', 'network', 'error'], action.error),
    ),

  [editTeam.success]: (state, action) =>
    state.setIn(['teamsById', action.response.id], Team.deserialize(action.response).normalize()),

  [viewTeam.request]: (state, action) =>
    state.setIn(['teams', action.teamId, 'network', 'loading'], true),
  [viewTeam.success]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['teams', action.teamId, 'network', 'loading'], false)
        .setIn(['teams', action.teamId, 'network', 'error'], false)
        .setIn(['teams', 'id'], action.response.id)
        .mergeIn(['membersById'], normalizeModelList(action.response.users, TeamMember.deserialize))
        .mergeIn(
          ['teamsById'],
          normalizeModelList([action.response], (...args) => Team.deserialize(...args).normalize()),
        ),
    ),
  [viewTeam.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['teams', action.teamId, 'network', 'loading'], false)
        .setIn(['teams', action.teamId, 'network', 'error'], action.error),
    ),

  [SET_COMPANY]: (state, action) =>
    state.withMutations((s) =>
      s
        // Save the company on the byId object
        .setIn(['companiesById', action.company.id], Entity.deserialize(action.company).normalize())
        // Save the company teams on the byId object
        .mergeIn(
          ['teamsById'],
          normalizeModelList(action.company.teams, (...args) =>
            Team.deserialize(...args).normalize(),
          ),
        )
        // Save the company team members on the byId object
        .mergeIn(
          ['membersById'],
          normalizeModelList(
            action.company.teams.flatMap((team) => team.users || []),
            TeamMember.deserialize,
          ),
        )
        // Update the network with the new company id
        .updateIn(['networksById', action.networkId, 'entities'], (entityIds) =>
          List(entityIds).push(action.company.id).toSet().toList(),
        ),
    ),
  [SET_TEAM]: (state, action) =>
    state.withMutations((s) =>
      s
        // Save team on the byId object
        .setIn(['teamsById', action.team.id], Team.deserialize(action.team).normalize())
        // Save the team members on the byId object
        .mergeIn(['membersById'], normalizeModelList(action.team.users, TeamMember.deserialize))
        // Update the company with the new team id
        .updateIn(['companiesById', action.companyId, 'teams'], (teamIds) =>
          List(teamIds).push(action.team.id).toSet().toList(),
        ),
    ),
  [SET_TEAM_MEMBERS]: (state, action) =>
    state.withMutations((s) =>
      s
        // Save members on the byId object
        .mergeIn(['membersById'], normalizeModelList(action.members, TeamMember.deserialize))
        // Update the team with the new members ids
        .updateIn(['teamsById', action.teamId, 'users'], (userIds) =>
          List(userIds)
            .push(...action.members.map((member) => member.id))
            .toSet()
            .toList(),
        ),
    ),
};

export default combineReducerArray(initialState, [
  teamReducerObject,
  updateRoleReducerObject,
  resendInviteReducerObject,
  makeChampionReducerObject,
  myCompanyTeamsReducerObject,
]);
